import { useState } from "react";
import { useToast } from "@/components/base/Toast";

type HealthStatus = "healthy" | "down" | "not_connected";

interface HealthEntry {
  id: string;
  name: string;
  status: HealthStatus;
  lastSuccess: string;
  successRate: number | null;
  avgResponse: string | null;
}

const initialHealthData: HealthEntry[] = [
  {
    id: "indiamart-api",
    name: "IndiaMart API",
    status: "healthy",
    lastSuccess: "2 mins ago",
    successRate: 99.2,
    avgResponse: "245ms",
  },
  {
    id: "justdial-webhook",
    name: "JustDial Webhook",
    status: "healthy",
    lastSuccess: "5 mins ago",
    successRate: 97.8,
    avgResponse: "180ms",
  },
  {
    id: "tradeindia-webhook",
    name: "TradeIndia Webhook",
    status: "down",
    lastSuccess: "1 hr ago",
    successRate: 43.2,
    avgResponse: "Timeout",
  },
  {
    id: "whatsapp-api",
    name: "WhatsApp API",
    status: "not_connected",
    lastSuccess: "Never",
    successRate: null,
    avgResponse: null,
  },
  {
    id: "website-forms",
    name: "Website Forms",
    status: "healthy",
    lastSuccess: "2 hrs ago",
    successRate: 100,
    avgResponse: "120ms",
  },
  {
    id: "exotel",
    name: "Exotel",
    status: "not_connected",
    lastSuccess: "Never",
    successRate: null,
    avgResponse: null,
  },
  {
    id: "facebook-leads",
    name: "Facebook Leads",
    status: "not_connected",
    lastSuccess: "Never",
    successRate: null,
    avgResponse: null,
  },
];

const statusConfig: Record<HealthStatus, { dot: string; label: string; labelClasses: string }> = {
  healthy: {
    dot: "bg-green-500",
    label: "Healthy",
    labelClasses: "bg-green-50 text-green-700",
  },
  down: {
    dot: "bg-red-500",
    label: "Down",
    labelClasses: "bg-red-50 text-red-600",
  },
  not_connected: {
    dot: "bg-gray-400",
    label: "Not Connected",
    labelClasses: "bg-gray-100 text-gray-600",
  },
};

function getRateColor(rate: number | null): string {
  if (rate === null) return "text-[#9ca3af]";
  if (rate > 95) return "text-green-600";
  if (rate >= 70) return "text-amber-600";
  return "text-red-600";
}

export function IntegrationHealthSection() {
  const { showToast } = useToast();
  const [healthData, setHealthData] = useState(initialHealthData);

  const handleTest = (entry: HealthEntry) => {
    showToast(`Testing ${entry.name} connection...`, "info");
    setTimeout(() => {
      if (entry.status === "healthy") {
        showToast(`${entry.name}: Connection successful`, "success");
      } else if (entry.status === "down") {
        showToast(`${entry.name}: Connection failed — endpoint unreachable`, "error");
      } else {
        showToast(`${entry.name}: Not connected — please connect first`, "info");
      }
    }, 1000);
  };

  const handleRefreshAll = () => {
    showToast("Refreshing all integration health checks...", "info");
    setTimeout(() => {
      showToast("All health checks refreshed", "success");
    }, 1500);
  };

  return (
    <div className="bg-white rounded-xl border border-[#e5e7eb] p-5">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
        <div>
          <h3 className="text-lg font-semibold text-[#111827]">Integration Health</h3>
          <p className="text-sm text-[#6b7280] mt-0.5">
            Real-time status of all connections
          </p>
        </div>
        <button
          onClick={handleRefreshAll}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-xs font-medium text-[#374151] rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
        >
          <i className="ri-refresh-line" />
          Refresh All
        </button>
      </div>

      {/* Health Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[680px]">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#374151]">
                Integration
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#374151]">
                Status
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#374151]">
                Last Success
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#374151]">
                Success Rate
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#374151]">
                Avg Response
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#374151]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {healthData.map((entry) => {
              const sc = statusConfig[entry.status];
              return (
                <tr key={entry.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-[#111827]">{entry.name}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${sc.dot}`} />
                      <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${sc.labelClasses}`}
                      >
                        {sc.label}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-[#6b7280]">{entry.lastSuccess}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-sm font-medium ${getRateColor(entry.successRate)}`}
                    >
                      {entry.successRate !== null ? `${entry.successRate}%` : "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-[#6b7280]">
                      {entry.avgResponse ?? "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {entry.status === "healthy" && (
                        <>
                          <button
                            onClick={() => handleTest(entry)}
                            className="px-2.5 py-1 border border-indigo-600 text-indigo-600 text-xs font-medium rounded-lg hover:bg-indigo-50 transition-colors whitespace-nowrap"
                          >
                            Test
                          </button>
                          <button
                            onClick={() => {
                              const activitySection = document.getElementById("integration-activity-log");
                              if (activitySection) {
                                activitySection.scrollIntoView({ behavior: "smooth" });
                              }
                              showToast("Viewing logs for " + entry.name, "info");
                            }}
                            className="px-2.5 py-1 border border-gray-300 text-[#374151] text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                          >
                            Logs
                          </button>
                        </>
                      )}
                      {entry.status === "down" && (
                        <>
                          <button
                            onClick={() => {
                              showToast(`Reconnecting ${entry.name}...`, "info");
                              setTimeout(() => {
                                setHealthData((prev) =>
                                  prev.map((e) =>
                                    e.id === entry.id
                                      ? {
                                          ...e,
                                          status: "healthy" as HealthStatus,
                                          lastSuccess: "Just now",
                                          successRate: 98.5,
                                          avgResponse: "210ms",
                                        }
                                      : e
                                  )
                                );
                                showToast(`${entry.name} reconnected successfully`, "success");
                              }, 1200);
                            }}
                            className="px-2.5 py-1 border border-red-500 text-red-500 text-xs font-medium rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap"
                          >
                            Reconnect
                          </button>
                          <button
                            onClick={() => {
                              const activitySection = document.getElementById("integration-activity-log");
                              if (activitySection) {
                                activitySection.scrollIntoView({ behavior: "smooth" });
                              }
                              showToast("Viewing logs for " + entry.name, "info");
                            }}
                            className="px-2.5 py-1 border border-gray-300 text-[#374151] text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                          >
                            Logs
                          </button>
                        </>
                      )}
                      {entry.status === "not_connected" && (
                        <button
                          onClick={() => showToast(`${entry.name} connected successfully!`, "success")}
                          className="px-2.5 py-1 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
                        >
                          Connect
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}